// @ts-check
// Estado dos CEPs que a tela mostra (CEP na família, contrato 07, seção 9.9.2): encontrado ou não encontrado, lido em
// lotes da tabela de CEPs da escola (GET /rest/address/postcode-statuses), sem consulta externa. A linha CEP, a coluna
// CEP e a indicação no card leem daqui para avisar o CEP que não existe, que tem 8 dígitos e passava por certo; os
// editores guardam aqui o resultado da própria consulta.
import { signal } from '@preact/signals';
import { api } from './api-client.js';
import { readPostcodeDigits } from '../../../shared/address/postcode-autofill.js';

/** @typedef {'FOUND' | 'NOT_FOUND'} PostcodeStatus */

const BATCH_DELAY_MILLISECONDS = 40;
const BATCH_SIZE = 100;
// CEP que a escola ainda não consultou: o cron resolve a cada 15 minutos, então pergunta de novo depois de um tempo
const UNKNOWN_RETRY_MILLISECONDS = 5 * 60 * 1000;

/** @type {import('@preact/signals').Signal<Map<string, PostcodeStatus>>} */
export const postcodeStatusesState = signal(new Map());

/** @type {Map<string, number>} */
const requestedAt = new Map();
/** @type {Set<string>} */
const queued = new Set();
/** @type {ReturnType<typeof setTimeout> | null} */
let flushTimer = null;
let isDenied = false;

/** @param {Record<string, PostcodeStatus>} statuses */
function mergeStatuses(statuses) {
  const entries = Object.entries(statuses ?? {}).filter(([, status]) => status === 'FOUND' || status === 'NOT_FOUND');
  if (entries.length === 0) return;
  const next = new Map(postcodeStatusesState.value);
  for (const [digits, status] of entries) next.set(digits, status);
  postcodeStatusesState.value = next;
}

async function flush() {
  flushTimer = null;
  const batch = [...queued].slice(0, BATCH_SIZE);
  for (const digits of batch) queued.delete(digits);
  if (queued.size > 0) flushTimer = setTimeout(flush, BATCH_DELAY_MILLISECONDS);
  if (batch.length === 0) return;
  try {
    const body = await api.get(`/rest/address/postcode-statuses?postcodes=${batch.join(',')}`);
    mergeStatuses(body?.data?.statuses ?? {});
  } catch (/** @type {any} */ error) {
    // papel que não lê endereço nenhum: para de perguntar nesta sessão
    if (error?.statusCode === 403) isDenied = true;
  }
}

/** @param {string} digits */
function request(digits) {
  if (isDenied) return;
  const last = requestedAt.get(digits);
  if (last !== undefined && Date.now() - last < UNKNOWN_RETRY_MILLISECONDS) return;
  requestedAt.set(digits, Date.now());
  queued.add(digits);
  if (!flushTimer) flushTimer = setTimeout(flush, BATCH_DELAY_MILLISECONDS);
}

/**
 * Estado conhecido do CEP (null enquanto não se sabe, ou CEP sem 8 dígitos). Lê o signal, então o componente que
 * chama volta a desenhar quando o estado chega; o pedido ao servidor sai em lote, fora da renderização.
 * @param {unknown} postcode
 * @returns {PostcodeStatus | null}
 */
export function readPostcodeStatus(postcode) {
  const digits = readPostcodeDigits(postcode);
  if (!digits) return null;
  const status = postcodeStatusesState.value.get(digits) ?? null;
  if (status === null) request(digits);
  return status;
}

/**
 * Resultado de uma consulta feita pelo editor (o servidor grava a mesma geografia em segundo plano).
 * @param {unknown} postcode @param {PostcodeStatus} status
 */
export function rememberPostcodeStatus(postcode, status) {
  const digits = readPostcodeDigits(postcode);
  if (!digits || postcodeStatusesState.value.get(digits) === status) return;
  requestedAt.set(digits, Date.now());
  mergeStatuses({ [digits]: status });
}
